import { pb } from "@/lib/pocketbase";
import { createStore } from "@/app/store";
import type { User } from "@/types/models";

/**
 * Auth-Schicht. Die UI nutzt ausschließlich das `auth`-Objekt, das gegen das
 * `AuthBackend`-Interface programmiert ist. Ein Wechsel auf ein anderes
 * Backend (eigene API, Supabase, ...) = eine neue Implementierung dieses
 * Interfaces, ohne die UI anzufassen. Aktuelle Implementierung: PocketBase
 * (E-Mail/Passwort + OAuth2).
 */

export interface OAuthProvider {
  /** Technischer Name, z. B. "google", "github". */
  name: string;
  /** Anzeigename für den Button. */
  displayName: string;
}

export interface AuthBackend {
  isAuthenticated(): boolean;
  currentUser(): User | null;
  login(email: string, password: string): Promise<User>;
  register(data: {
    email: string;
    password: string;
    passwordConfirm: string;
    name?: string;
  }): Promise<User>;
  logout(): void;
  /** In PocketBase konfigurierte OAuth2-Provider (leer, falls keine). */
  listOAuthProviders(): Promise<OAuthProvider[]>;
  loginWithOAuth(provider: string): Promise<User>;
}

interface AuthState {
  user: User | null;
}

/** Liest den aktuellen Auth-Record (SDK-versionssicher: record | model). */
function currentRecord(): User | null {
  const store = pb.authStore as unknown as {
    record?: User | null;
    model?: User | null;
  };
  return store.record ?? store.model ?? null;
}

/** Reaktiver Auth-Store, auf den die UI hört. */
export const authStore = createStore<AuthState>({ user: currentRecord() });

pb.authStore.onChange(() => {
  authStore.set({ user: currentRecord() });
});

/**
 * Sitzung von selbst am Leben halten, statt sie einfach ablaufen zu lassen.
 * Ohne das galt der Token nur bis zu seiner festen Lebenszeit (PocketBase-
 * Standard 7 Tage) – bei einem über Stunden offenen Tab (ein Schultag voller
 * Notizen) lief die Sitzung dann mitten in der Arbeit klanglos ab, ohne dass
 * je ein `logout()` aufgerufen wurde: der nächste Speicherversuch schlug
 * einfach fehl. Ein fehlgeschlagener Refresh meldet sich NICHT ab – bewusst,
 * siehe AppLayout.ts (nur der Abmelden-Knopf ruft `logout()`).
 */
let refreshing = false;
async function refreshSession(): Promise<void> {
  if (refreshing || !pb.authStore.isValid) return;
  refreshing = true;
  try {
    await pb.collection("users").authRefresh();
  } catch {
    // Netz weg oder Token wirklich ungültig – beides kein Grund, die Sitzung
    // stillschweigend zu killen. Der nächste Schreibversuch zeigt dann eine
    // klare Fehlermeldung (siehe pbErrorMessage in lib/pocketbase.ts).
  } finally {
    refreshing = false;
  }
}
const REFRESH_INTERVAL_MS = 5 * 60_000;
window.setInterval(() => void refreshSession(), REFRESH_INTERVAL_MS);
// Zusätzlich sofort beim Zurückkehren aus dem Hintergrund/Ruhezustand –
// genau der Fall, in dem am meisten Zeit unbemerkt vergangen sein kann.
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible") void refreshSession();
});

// ---- PocketBase-Implementierung des AuthBackend ----------------------------

const pocketbaseAuth: AuthBackend = {
  isAuthenticated() {
    return pb.authStore.isValid;
  },

  currentUser() {
    return currentRecord();
  },

  async login(email, password) {
    const res = await pb
      .collection("users")
      .authWithPassword(email.trim(), password);
    return res.record as unknown as User;
  },

  async register(data) {
    const created = await pb.collection("users").create({
      email: data.email.trim(),
      password: data.password,
      passwordConfirm: data.passwordConfirm,
      name: data.name?.trim() || "",
    });
    await pb
      .collection("users")
      .authWithPassword(data.email.trim(), data.password);
    return created as unknown as User;
  },

  logout() {
    pb.authStore.clear();
  },

  async listOAuthProviders() {
    try {
      const methods = (await pb
        .collection("users")
        .listAuthMethods()) as unknown as {
        oauth2?: { enabled?: boolean; providers?: { name: string; displayName?: string }[] };
        authProviders?: { name: string; displayName?: string }[]; // ältere SDKs
      };
      // Ist OAuth2 in PocketBase abgeschaltet, gibt es nichts anzuzeigen.
      if (methods.oauth2 && methods.oauth2.enabled === false) return [];
      const list = methods.oauth2?.providers ?? methods.authProviders ?? [];
      const providers = list.map((p) => ({
        name: p.name,
        displayName: p.displayName || p.name,
      }));
      // Gängige Anbieter zuerst, der Rest in PocketBase-Reihenfolge.
      const order = ["google", "apple", "microsoft"];
      return providers.sort((a, b) => {
        const ia = order.indexOf(a.name.toLowerCase());
        const ib = order.indexOf(b.name.toLowerCase());
        return (ia === -1 ? order.length : ia) - (ib === -1 ? order.length : ib);
      });
    } catch {
      // z. B. wenn Backend nicht erreichbar ist -> einfach keine Provider zeigen
      return [];
    }
  },

  async loginWithOAuth(provider) {
    // All-in-one-Flow (öffnet ein Popup). Für Web ausreichend; für native
    // Capacitor-Apps ist später ein Browser-Plugin/Redirect-Flow nötig (V2).
    const res = await pb.collection("users").authWithOAuth2({ provider });
    return res.record as unknown as User;
  },
};

export const auth: AuthBackend = pocketbaseAuth;
